import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "Store/configureStore";
import { WizardStepperStepStatus } from "@packages/ds";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { CustomerType } from "Api/billingSystem";
import type { ProfileDetail } from "Api/billingSystem/types";
import {
  getOrganization,
  organizationByNameOrIdSelector
} from "Reducers/organization/organization";
import { getUserBillingAccounts, selectUserBillingAccounts } from "./accounts";
import { getUserBillingProfileDetailsThunk } from "./profiles";
import { getProfilePaymentSources } from "./paymentSources";
import { getContracts, selectProfileHasActiveCommitments } from "./contracts";
import { selectEligibleOrganizationsForBillingProfile } from "./attachEligibleOrganization";

type BillingWizardStep =
  | "select"
  | "overview"
  | "type"
  | "legal"
  | "billing"
  | "payment"
  | "linkOrganizations";

type BillingWizardStepperStep = {
  id: BillingWizardStep;
  hide?: boolean;
  label?: string;
  status: WizardStepperStepStatus;
};

const steps: Record<BillingWizardStep, BillingWizardStepperStep> = {
  /** Step to select the billing entity/account if the user already has one */
  select: {
    hide: true,
    id: "select",
    status: "default"
  },
  /** This is the subscribe flow overview */
  overview: {
    hide: true,
    id: "overview",
    status: "default"
  },
  /** Chooses the billing type. Based on this billing type we'll display a
   * different set of fields on the legal step */
  type: {
    id: "type",
    label: "billingsystem.wizard.steps.type",
    status: "default"
  },
  /** Form containing legal information about the billing entity */
  legal: {
    id: "legal",
    label: "billingsystem.wizard.steps.legal",
    status: "default"
  },
  /** Billing details for the billing profile */
  billing: {
    id: "billing",
    label: "billingsystem.wizard.steps.billing",
    status: "default"
  },
  /** Payment information, attached to a billing profile */
  payment: {
    id: "payment",
    label: "billingsystem.wizard.steps.payment",
    status: "default"
  },
  /** Optionally link the user's eligible organizations to the new profile.
   * Hidden from the stepper — it's a terminal, full-screen prompt. */
  linkOrganizations: {
    hide: true,
    id: "linkOrganizations",
    status: "default"
  }
};

const resetSteps = (steps: Array<BillingWizardStepperStep>) => {
  return steps.map((step, index) => ({
    ...step,
    status: index === 0 ? ("active" as const) : ("default" as const)
  }));
};

export type BillingSystemWizardState = {
  isOpen: boolean;

  /** Based on this type, we'll display different kind of fields on the legal
   * step */
  billingAccountType?: CustomerType;

  /** In some situations we'll have to attach an organization to a billing
   * account after creating it */
  organizationId?: string;
  billingAccountId?: string;
  billingProfileId?: string;

  /** Pricing kind chosen for the billing profile being created. Used to filter
   * the organizations offered on the link-organizations step to matching ones. */
  pricingModel?: ProfileDetail["pricing_model"];

  isNewOrganization?: boolean;

  steps: Array<BillingWizardStepperStep>;
};

const initialState: BillingSystemWizardState = {
  isOpen: false,
  steps: []
};

const updateStep = (
  state: BillingSystemWizardState,
  id: BillingWizardStep,
  updates: Partial<BillingWizardStepperStep>
) => {
  const index = state.steps.findIndex(step => step.id === id);
  if (index !== -1) state.steps[index] = { ...state.steps[index], ...updates };
};

const slice = createSlice({
  name: "billingSystemWizard",
  initialState,
  reducers: {
    /** Opens the wizard to allow to select an existing billing entity/account
     * for an organization, then collect the profile's payment method */
    billingSystemWizardOpenSelect(
      state,
      action: PayloadAction<{
        organizationId: string;
        isNewOrganization?: boolean;
      }>
    ) {
      state.isOpen = true;
      state.organizationId = action.payload.organizationId;
      state.isNewOrganization = !!action.payload.isNewOrganization;
      state.steps = resetSteps([
        steps.select,
        steps.payment,
        steps.linkOrganizations
      ]);
    },
    /** Opens the wizard for the subscribe flow */
    billingSystemWizardOpenSubscribe(
      state,
      action: PayloadAction<{ organizationId: string }>
    ) {
      state.isOpen = true;
      state.organizationId = action.payload.organizationId;
      state.steps = resetSteps([
        steps.overview,
        steps.type,
        steps.legal,
        steps.billing,
        steps.payment,
        steps.linkOrganizations
      ]);
    },
    /** Opens the wizard to only create a billing account */
    billingSystemWizardOpenCreateBillingAccount(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.isOpen = true;
      state.organizationId = action.payload;
      state.steps = resetSteps([
        steps.type,
        steps.legal,
        steps.billing,
        steps.payment,
        steps.linkOrganizations
      ]);
    },
    /**
     * Opens the wizard to only create a account. Requires an existing billing
     * entity. The optional pricing model preselects the organization-type
     * dropdown on the billing step — used when the select step prompts the
     * user to create a profile compatible with their organization.
     */
    billingSystemWizardOpenCreateBillingProfile: {
      prepare(
        billingAccountId: string,
        pricingModel?: ProfileDetail["pricing_model"]
      ) {
        return { payload: { billingAccountId, pricingModel } };
      },
      reducer(
        state,
        action: PayloadAction<{
          billingAccountId: string;
          pricingModel?: ProfileDetail["pricing_model"];
        }>
      ) {
        state.billingAccountId = action.payload.billingAccountId;
        state.pricingModel = action.payload.pricingModel;
        state.isOpen = true;
        state.steps = resetSteps([
          steps.billing,
          steps.payment,
          steps.linkOrganizations
        ]);
      }
    },
    /**
     * Opens the wizard directly on the payment step, resuming an interrupted
     * run that linked the organization to a billing profile but never
     * collected a payment method
     */
    billingSystemWizardOpenPayment(
      state,
      action: PayloadAction<{
        organizationId: string;
        billingAccountId: string;
        billingProfileId: string;
        pricingModel?: ProfileDetail["pricing_model"];
      }>
    ) {
      state.isOpen = true;
      state.organizationId = action.payload.organizationId;
      state.billingAccountId = action.payload.billingAccountId;
      state.billingProfileId = action.payload.billingProfileId;
      state.pricingModel = action.payload.pricingModel;
      state.steps = resetSteps([steps.payment, steps.linkOrganizations]);
    },
    /** Closes and resets the billing entity wizard */
    closeBillingSystemWizard(state) {
      state.isOpen = false;
      state.steps = [];
      state.organizationId = undefined;
      state.billingAccountId = undefined;
      state.billingProfileId = undefined;
      state.billingAccountType = undefined;
      state.pricingModel = undefined;
      state.isNewOrganization = undefined;
    },
    billingSystemWizardSetBillingAccountType(
      state,
      action: PayloadAction<CustomerType>
    ) {
      state.billingAccountType = action.payload;
    },

    /** Advances to the next step in the current flow */
    billingSystemWizardNextStep(
      state,
      action: PayloadAction<
        | {
            billingAccountId?: string;
            billingProfileId?: string;
            pricingModel?: ProfileDetail["pricing_model"];
          }
        | undefined
      >
    ) {
      const currentIndex = state.steps.findIndex(
        step => step.status === "active"
      );
      if (currentIndex === -1 || currentIndex === state.steps.length - 1) {
        return; // No active step or already at the last step
      }

      // Store IDs if provided
      if (action.payload?.billingAccountId) {
        state.billingAccountId = action.payload.billingAccountId;
      }
      if (action.payload?.billingProfileId) {
        state.billingProfileId = action.payload.billingProfileId;
      }
      if (action.payload?.pricingModel) {
        state.pricingModel = action.payload.pricingModel;
      }

      // Move to next step
      const currentStep = state.steps[currentIndex];
      const nextStep = state.steps[currentIndex + 1];
      updateStep(state, currentStep.id, { status: "completed" });
      updateStep(state, nextStep.id, { status: "active" });
    },

    /** Drops a queued step from the current flow so the next advance lands on
     * the step after it. No-op for the active step. */
    billingSystemWizardSkipStep(
      state,
      action: PayloadAction<BillingWizardStep>
    ) {
      state.steps = state.steps.filter(
        step => step.id !== action.payload || step.status === "active"
      );
    },

    billingSystemWizardSetBillingEntityId(
      state,
      action: PayloadAction<string>
    ) {
      state.billingAccountId = action.payload;
    },
    billingSystemWizardSetBillingProfileId(
      state,
      action: PayloadAction<string>
    ) {
      state.billingProfileId = action.payload;
    }
  }
});

export const billingSystemWizardReducer = slice.reducer;

export const {
  billingSystemWizardSetBillingProfileId,
  billingSystemWizardSetBillingEntityId,

  billingSystemWizardOpenSelect,
  billingSystemWizardOpenCreateBillingProfile,
  billingSystemWizardOpenSubscribe,
  billingSystemWizardOpenCreateBillingAccount,
  billingSystemWizardOpenPayment,

  closeBillingSystemWizard,

  billingSystemWizardSetBillingAccountType,

  billingSystemWizardNextStep,
  billingSystemWizardSkipStep
} = slice.actions;

/**
 * Whether a billing profile still has to collect a payment method. Profiles
 * that already have one, or that are covered by an active contract (e.g. LTC
 * customers, who never add a card), don't. Both endpoints are fetched fresh:
 * a payment method may have been added moments ago. When either fetch fails
 * we can't tell, so we err on asking for one.
 */
export const billingProfileNeedsPaymentMethod = createAppAsyncThunk(
  "billingSystemWizard/billingProfileNeedsPaymentMethod",
  async (
    { billingProfileId }: { billingProfileId: string },
    { dispatch, getState }
  ) => {
    const [paymentSources, contracts] = await Promise.all([
      dispatch(getProfilePaymentSources({ billingProfileId }))
        .unwrap()
        .catch(() => undefined),
      dispatch(getContracts({ billingProfileId }))
        .unwrap()
        .catch(() => undefined)
    ]);

    const hasPaymentMethod = !!paymentSources?.payment_source?.type;
    const hasActiveContract =
      !!contracts &&
      selectProfileHasActiveCommitments(getState(), billingProfileId);

    return !hasPaymentMethod && !hasActiveContract;
  }
);

export type BillingWizardSelectOutcome =
  "payment" | "linkOrganizations" | "complete";

/**
 * Decides where the select flow goes once an organization has been assigned to
 * an existing billing profile. The payment step is only worth showing when the
 * profile has no way to pay yet; otherwise it's skipped, landing on the
 * link-organizations step when the user has other organizations to link, or
 * completing the wizard when there's nothing left to do.
 */
export const billingSystemWizardContinueFromSelect = createAppAsyncThunk(
  "billingSystemWizard/continueFromSelect",
  async (
    {
      billingProfileId,
      pricingModel
    }: {
      billingProfileId: string;
      pricingModel?: ProfileDetail["pricing_model"];
    },
    { dispatch, getState }
  ): Promise<BillingWizardSelectOutcome> => {
    const needsPaymentMethod = await dispatch(
      billingProfileNeedsPaymentMethod({ billingProfileId })
    ).unwrap();

    if (needsPaymentMethod) {
      dispatch(billingSystemWizardNextStep({ billingProfileId, pricingModel }));
      return "payment";
    }

    dispatch(billingSystemWizardSkipStep("payment"));

    const eligibleOrganizations = selectEligibleOrganizationsForBillingProfile(
      getState(),
      pricingModel
    );
    if (eligibleOrganizations.length > 0) {
      dispatch(billingSystemWizardNextStep({ billingProfileId, pricingModel }));
      return "linkOrganizations";
    }

    // Completion (navigate + close) needs the router, so the step handles it;
    // record the ids it relies on.
    dispatch(billingSystemWizardSetBillingProfileId(billingProfileId));
    return "complete";
  }
);

/** Based on whether the current user has already billing entities, open the
 * wizard on the correct step.
 * If the user has already billing entities will allow the customer to select an
 * account, otherwise will display the default billing entity creation flow */
export const billingSystemWizardOpenBasedOnEntities = createAppAsyncThunk(
  "billingSystemWizard/billingSystemWizardOpenSelect",
  async (
    props: { isNewOrganization?: boolean; organizationId: string },
    { getState, dispatch }
  ) => {
    const currentOrganization = props.organizationId;
    await dispatch(getUserBillingAccounts());
    const billingAccounts = selectUserBillingAccounts(getState());
    const hasBillingAccounts = !!billingAccounts.length;

    if (!hasBillingAccounts) {
      dispatch(
        billingSystemWizardOpenCreateBillingAccount(currentOrganization)
      );
      return;
    }

    // An organization already linked to a billing profile has nothing to
    // select — an earlier interrupted run may have linked it without ever
    // reaching the payment step (creating the billing account or profile
    // revokes the token, so a forced re-login mid-flow drops the user before
    // payment), and the select screen could only re-assign the same profile.
    // Open straight on the payment step for the linked profile instead. Falls
    // through to the select screen when the linked profile isn't among the
    // user's own (e.g. staff-managed profiles).
    // getOrganization resolves from the store when cached, so no pre-check.
    await dispatch(
      getOrganization({ organizationDescriptionId: currentOrganization })
    )
      .unwrap()
      .catch(() => undefined);
    const organization = organizationByNameOrIdSelector(getState(), {
      organizationNameOrId: currentOrganization
    });

    const linkedProfileId = organization?.billing_profile_id;
    if (linkedProfileId) {
      // Decide from the fresh, user-scoped response rather than the shared
      // profile store: the cached list may predate the linked profile, and
      // the entity store holds any profile ever loaded, not only the user's.
      const profiles = await dispatch(
        getUserBillingProfileDetailsThunk({ forceRefetch: true })
      )
        .unwrap()
        .catch(() => undefined);
      const profile = profiles?.items.find(
        ({ billing_profile_id }) => billing_profile_id === linkedProfileId
      );

      if (profile) {
        dispatch(
          billingSystemWizardOpenPayment({
            organizationId: currentOrganization,
            billingAccountId: profile.billing_account_id,
            billingProfileId: profile.billing_profile_id,
            pricingModel: profile.pricing_model
          })
        );
        return;
      }
    }

    dispatch(
      billingSystemWizardOpenSelect({
        organizationId: currentOrganization,
        isNewOrganization: !!props?.isNewOrganization
      })
    );
  }
);

export const billingSystemWizardSelector = (state: RootState) =>
  state.billingSystemWizard;

export const billingSystemWizardIsOpenSelector = createSelector(
  billingSystemWizardSelector,
  state => state.isOpen
);

export const billingSystemWizardStepsSelector = createSelector(
  billingSystemWizardSelector,
  state => state.steps
);

export const billingSystemWizardCurrentStepSelector = createSelector(
  billingSystemWizardStepsSelector,
  steps => steps.find(step => step.status === "active")
);

export const billingSystemWizardOrganizationSelector = createSelector(
  billingSystemWizardSelector,
  state => state.organizationId
);

export const billingSystemWizardBillingEntityIdSelector = createSelector(
  billingSystemWizardSelector,
  state => state.billingAccountId
);

export const billingSystemWizardBillingProfileIdSelector = createSelector(
  billingSystemWizardSelector,
  state => state.billingProfileId
);

export const billingSystemWizardIsNewOrganization = createSelector(
  billingSystemWizardSelector,
  state => state.isNewOrganization
);

export const billingSystemWizardBillingAccountTypeSelector = createSelector(
  billingSystemWizardSelector,
  state => state.billingAccountType
);

export const billingSystemWizardPricingModelSelector = createSelector(
  billingSystemWizardSelector,
  state => state.pricingModel
);
